import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

export default function MoreActionsDropdown({
  label = "More",
  items = [],
  buttonClassName = "",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (event) => {
      if (
        menuRef.current?.contains(event.target) ||
        btnRef.current?.contains(event.target)
      ) {
        return;
      }
      setOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", handleClick);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handleClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const renderedItems = items.filter(Boolean);
  if (renderedItems.length === 0) return null;

  const toggle = () => {
    if (disabled) return;
    setOpen((prev) => !prev);
  };

  return (
    <div className={`dropdown ${buttonClassName}`.trim()}>
      <button
        type="button"
        className="btn btn-outline-secondary rounded-pill d-inline-flex align-items-center gap-2"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggle}
        ref={btnRef}
        disabled={disabled}
      >
        <span>{label}</span>
        <i className={`bi ${open ? "bi-caret-up-fill" : "bi-caret-down-fill"}`} aria-hidden="true" />
      </button>
      {open && (
        <div
          className="dropdown-menu dropdown-menu-end show shadow"
          role="menu"
          ref={menuRef}
        >
          {renderedItems.map((item, idx) => {
            if (item.type === "divider") {
              return <div key={`divider-${idx}`} className="dropdown-divider" />;
            }
            const itemClass = [
              "dropdown-item",
              item.danger ? "text-danger" : "",
              item.disabled ? "disabled" : "",
            ]
              .filter(Boolean)
              .join(" ");

            return (
              <button
                key={item.key || item.label || idx}
                type="button"
                className={itemClass}
                role="menuitem"
                onClick={() => {
                  if (item.disabled) return;
                  item.onClick?.();
                  setOpen(false);
                }}
              >
                {item.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

MoreActionsDropdown.propTypes = {
  label: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        onClick: PropTypes.func,
        danger: PropTypes.bool,
        disabled: PropTypes.bool,
        key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      }),
      PropTypes.shape({
        type: PropTypes.oneOf(["divider"]).isRequired,
      }),
    ])
  ),
  buttonClassName: PropTypes.string,
  disabled: PropTypes.bool,
};
